import { useState, useCallback } from 'react';
import { notify } from '../../../utils/notifications';
import api from '../../../api/api';
import DraggableList from '../shared/DraggableList';
import EntityPicker from '../shared/EntityPicker';
import PromptEditor from '../shared/PromptEditor';
import ConfirmDialog from '../shared/ConfirmDialog';
import '../WorkflowEditor.css';

function DragHandle({ listeners }) {
  return (
    <div className="wfe-drag-handle" {...listeners}>
      <div className="wfe-drag-dots">
        <span /><span /><span /><span /><span /><span />
      </div>
    </div>
  );
}

function FieldRow({ field, index, groupIndex, dispatch, allConfigs, allFields, dragListeners }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editQuestion, setEditQuestion] = useState('');
  const [editRequired, setEditRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const usedByCount = allConfigs.filter((c) =>
    (c.extraction || []).some((g) => (g.field_refs || []).includes(field?.id))
  ).length;

  const startEdit = () => {
    if (!field) return;
    setEditName(field.name || '');
    setEditQuestion(field.question || '');
    setEditRequired(field.required || false);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await api.put(`/admin/extraction-fields/${field.id}`, {
        name: editName,
        question: editQuestion,
        required: editRequired,
      });
      dispatch({ type: 'UPDATE_FIELD', field: resp.data });
      notify.success('Field updated');
      setEditing(false);
    } catch {
      notify.error('Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  if (!field) {
    return (
      <div className="wfe-field-row wfe-field-missing">
        <DragHandle listeners={dragListeners} />
        <span className="wfe-field-name" style={{ color: '#DC2626' }}>
          Missing field reference
        </span>
        <div className="wfe-field-actions-visible">
          <button
            className="wfe-field-btn danger"
            onClick={() => dispatch({ type: 'REMOVE_FIELD_FROM_GROUP', groupIndex, fieldIndex: index })}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`wfe-field-row ${editing ? 'wfe-field-editing' : ''}`}>
        <DragHandle listeners={dragListeners} />
        <span className="wfe-field-name">{field.name}</span>
        {field.required && <span className="wfe-required-badge">Required</span>}
        {!editing && (
          <span className="wfe-field-preview">
            {field.question ? `"${field.question.slice(0, 50)}..."` : ''}
          </span>
        )}
        <div className={editing ? 'wfe-field-actions-visible' : 'wfe-field-actions'}>
          {editing ? (
            <button className="wfe-field-btn" style={{ color: '#0D9488', fontWeight: 500 }}>Editing</button>
          ) : (
            <button className="wfe-field-btn" onClick={startEdit}>Edit</button>
          )}
          <button
            className="wfe-field-btn danger"
            onClick={() => setConfirmRemove(true)}
          >
            Remove
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove "${field.name}"?`}
        message="This removes the field from this group. The field definition will still exist and can be re-added later."
        confirmLabel="Remove"
        onConfirm={() => { setConfirmRemove(false); dispatch({ type: 'REMOVE_FIELD_FROM_GROUP', groupIndex, fieldIndex: index }); }}
        onCancel={() => setConfirmRemove(false)}
      />

      {editing && (
        <div className="wfe-inline-edit">
          {usedByCount > 1 && (
            <div className="wfe-shared-warning">
              <span>&#9888;</span>
              <span>
                This field is used by <strong>{usedByCount} facility configs</strong>. Changes will affect all of them.
              </span>
            </div>
          )}

          <div className="wfe-form-group" style={{ marginBottom: 14 }}>
            <label className="wfe-form-label">Field Name</label>
            <input className="wfe-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <PromptEditor
              label="Extraction Question"
              value={editQuestion}
              onChange={setEditQuestion}
              rows={3}
              showStats={true}
            />
          </div>

          <div className="wfe-form-group" style={{ marginBottom: 14 }}>
            <div className="wfe-toggle-row">
              <button
                className={`wfe-toggle ${editRequired ? 'on' : ''}`}
                onClick={() => setEditRequired(!editRequired)}
                type="button"
              />
              <span className="wfe-toggle-label">Required field</span>
            </div>
          </div>

          <div className="wfe-inline-edit-footer">
            <button className="wfe-btn-sm primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Field'}
            </button>
            <button className="wfe-btn-sm ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}

function GroupCard({ group, groupIndex, dispatch, allFields, allConfigs, configs, dragListeners }) {
  const [expanded, setExpanded] = useState(groupIndex === 0);
  const [showPicker, setShowPicker] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newField, setNewField] = useState({ name: '', question: '', required: false });
  const [creating, setCreating] = useState(false);
  const [confirmRemoveGroup, setConfirmRemoveGroup] = useState(false);

  const fields = (group.field_refs || []).map((id) => allFields.find((f) => f.id === id));

  const handleCreateField = async () => {
    if (!newField.name.trim() || !newField.question.trim()) return;
    setCreating(true);
    try {
      const id = newField.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      const resp = await api.post('/admin/extraction-fields', {
        id,
        name: newField.name.trim(),
        question: newField.question.trim(),
        required: newField.required,
        category: group.group_name.toLowerCase().replace(/\s+/g, '_'),
      });
      dispatch({ type: 'ADD_FIELD', field: resp.data });
      dispatch({ type: 'ADD_FIELD_TO_GROUP', groupIndex, fieldId: resp.data.id });
      setNewField({ name: '', question: '', required: false });
      setShowCreateForm(false);
      notify.success('Field created and added');
    } catch (err) {
      if (err.response?.status === 409) {
        notify.error('A field with this ID already exists');
      } else {
        notify.error('Failed to create field');
      }
    } finally {
      setCreating(false);
    }
  };

  // Flatten all field refs across all configs for usage computation
  const getConfigFieldRefs = useCallback((config) => {
    return (config.extraction || []).flatMap((g) => g.field_refs || []);
  }, []);

  return (
    <div className="wfe-group">
      <div className="wfe-group-header" onClick={() => setExpanded(!expanded)}>
        <DragHandle listeners={dragListeners} />
        <div className="wfe-group-order">{group.order || groupIndex + 1}</div>
        <span className="wfe-group-name">{group.group_name}</span>
        <span className="wfe-group-count">{(group.field_refs || []).length} fields</span>
        <div className="wfe-group-actions">
          <span className={`wfe-chevron ${expanded ? 'open' : ''}`}>&#9662;</span>
          <button
            className="wfe-icon-btn danger"
            onClick={(e) => { e.stopPropagation(); setConfirmRemoveGroup(true); }}
            title="Remove group"
          >
            &times;
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemoveGroup}
        title={`Remove "${group.group_name}" group?`}
        message={`This will remove the group and its ${(group.field_refs || []).length} field references from this config. The field definitions will still exist.`}
        confirmLabel="Remove Group"
        onConfirm={() => { setConfirmRemoveGroup(false); dispatch({ type: 'REMOVE_GROUP', index: groupIndex }); }}
        onCancel={() => setConfirmRemoveGroup(false)}
      />

      {expanded && (
        <>
          <div className="wfe-group-body">
            <DraggableList
              items={group.field_refs || []}
              keyExtractor={(id) => id}
              onReorder={(newRefs) => dispatch({ type: 'REORDER_FIELDS_IN_GROUP', groupIndex, fieldRefs: newRefs })}
              renderItem={(fieldId, idx, { dragListeners: dl }) => (
                <FieldRow
                  field={allFields.find((f) => f.id === fieldId)}
                  index={idx}
                  groupIndex={groupIndex}
                  dispatch={dispatch}
                  allConfigs={configs}
                  allFields={allFields}
                  dragListeners={dl}
                />
              )}
            />
          </div>

          {showCreateForm && (
            <div className="wfe-inline-edit" style={{ borderTop: '1px solid #F0F0EA' }}>
              <div className="wfe-form-group" style={{ marginBottom: 14 }}>
                <label className="wfe-form-label">Field Name</label>
                <input
                  className="wfe-input"
                  placeholder="e.g., Patient First Name"
                  value={newField.name}
                  onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <PromptEditor
                  label="Extraction Question"
                  value={newField.question}
                  onChange={(q) => setNewField({ ...newField, question: q })}
                  rows={3}
                  placeholder="What is the patient's..."
                />
              </div>
              <div className="wfe-form-group" style={{ marginBottom: 14 }}>
                <div className="wfe-toggle-row">
                  <button
                    className={`wfe-toggle ${newField.required ? 'on' : ''}`}
                    onClick={() => setNewField({ ...newField, required: !newField.required })}
                    type="button"
                  />
                  <span className="wfe-toggle-label">Required field</span>
                </div>
              </div>
              <div className="wfe-inline-edit-footer">
                <button className="wfe-btn-sm primary" onClick={handleCreateField} disabled={creating}>
                  {creating ? 'Creating...' : 'Create & Add'}
                </button>
                <button className="wfe-btn-sm ghost" onClick={() => setShowCreateForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div className="wfe-group-footer">
            <button className="wfe-btn-add-sm" onClick={() => setShowCreateForm(true)}>
              + Create New Field
            </button>
            <button className="wfe-btn-add-sm" onClick={() => setShowPicker(true)}>
              + Add Existing Field
            </button>
          </div>

          <EntityPicker
            open={showPicker}
            onClose={() => setShowPicker(false)}
            title="Add Extraction Field"
            allEntities={allFields}
            excludeIds={group.field_refs || []}
            getLabel={(f) => f.name}
            getPreview={(f) => f.question ? `${f.question.slice(0, 80)}...` : ''}
            onSelect={(id) => dispatch({ type: 'ADD_FIELD_TO_GROUP', groupIndex, fieldId: id })}
            allConfigs={configs}
            getConfigRefs={getConfigFieldRefs}
          />
        </>
      )}
    </div>
  );
}

export default function ExtractionSection({ config, allFields, allConfigs, dispatch }) {
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  const groups = config.extraction || [];

  const handleAddGroup = () => {
    if (!newGroupName.trim()) return;
    dispatch({ type: 'ADD_GROUP', groupName: newGroupName.trim() });
    setNewGroupName('');
    setShowAddGroup(false);
  };

  return (
    <div>
      <div className="wfe-section-header">
        <span className="wfe-section-label">Extraction Groups</span>
        <button className="wfe-btn-add" onClick={() => setShowAddGroup(true)}>
          + Add Group
        </button>
      </div>

      {showAddGroup && (
        <div className="wfe-add-group-form">
          <input
            className="wfe-input"
            placeholder="Group name, e.g. Demographics"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddGroup()}
            autoFocus
          />
          <button className="wfe-btn-sm primary" onClick={handleAddGroup}>Add</button>
          <button className="wfe-btn-sm ghost" onClick={() => { setShowAddGroup(false); setNewGroupName(''); }}>Cancel</button>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="wfe-placeholder">
          <p>No extraction groups yet. Add a group to start configuring fields.</p>
        </div>
      ) : (
        <DraggableList
          items={groups}
          keyExtractor={(g) => g.group_name}
          onReorder={(newGroups) => dispatch({ type: 'REORDER_GROUPS', groups: newGroups })}
          renderItem={(group, idx, { dragListeners }) => (
            <GroupCard
              group={group}
              groupIndex={idx}
              dispatch={dispatch}
              allFields={allFields}
              allConfigs={allConfigs}
              configs={allConfigs}
              dragListeners={dragListeners}
            />
          )}
        />
      )}
    </div>
  );
}
